import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { commodityPageSize, useGroupedCommodities } from '@bechtel/client-common';
import { APPROVE_ALL_MILESTONES, CLAIM_ALL_MILESTONES, GET_COMMODITIES } from '@bechtel/client-gql';
import {
  Box,
  Button,
  ButtonAppearance,
  ButtonSize,
  Checkbox,
  Icon,
  Modal,
  ModalBodyStyled,
  ModalClose,
  ModalFooter,
  ModalSize,
  Spinner,
  SpinnerSize,
  Text,
} from '@bechtel/gds';
import {
  ApproveAllMilestonesError,
  ApproveAllMilestonesInput,
  ApproveAllMilestonesPayload,
  ApproveMilestoneError,
  BlockStatusCode,
  ClaimAllMilestonesError,
  ClaimAllMilestonesInput,
  ClaimAllMilestonesPayload,
  ClaimMilestoneError,
  CommodityFilterByTerm,
  CommodityFilterInput,
  CommodityList,
  CommodityMilestone,
  CommoditySortByTerm,
  CommodityStatusCode,
  CommodityType,
  FilterOperator,
  ItrMilestoneAction,
  ItrType,
  MaterialCommodity,
  MilestoneStatusCode,
  PagingInput,
  ProjectItrConfig,
  ProjectMilestoneType,
  SortOrder,
} from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { every, filter, flatMap, get, isEmpty, map, some, toLower, uniqBy } from 'lodash';
import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { useWorkPackage } from '~/detail/context';
import { ActionMilestone } from '..';
import { BulkActionError } from '../error';
import { CommodityMilestoneStyled, ModalHeaderStyled, ModalSubFooter, ModalSubHeader } from './styled';

interface MultipleCommodityActionProps {
  close: () => void;
  actionMilestone: ActionMilestone;
  commodityType: CommodityType;
  commodities: MaterialCommodity[];
}

const getClaimAllErroredMilestones = (erroredMilestones?: ClaimAllMilestonesError[] | null) => {
  if (!erroredMilestones || !erroredMilestones.length) return null;
  const filteredMilestones = filter(
    erroredMilestones,
    (milestone) =>
      milestone.errorCode === ClaimMilestoneError.NOT_FOUND ||
      milestone.errorCode === ClaimMilestoneError.EIS_ERROR ||
      milestone.errorCode === ClaimMilestoneError.MILESTONE_CONFIG_NOT_FOUND
  );

  return filteredMilestones.length ? filteredMilestones : null;
};

const getApproveAllErroredMilestones = (erroredMilestones?: ApproveAllMilestonesError[] | null) => {
  if (!erroredMilestones || !erroredMilestones.length) return null;
  const filteredMilestones = filter(
    erroredMilestones,
    (milestone) =>
      milestone.errorCode === ApproveMilestoneError.NOT_FOUND ||
      milestone.errorCode === ApproveMilestoneError.EIS_ERROR ||
      milestone.errorCode === ApproveMilestoneError.MILESTONE_CONFIG_NOT_FOUND
  );
  return filteredMilestones.length ? filteredMilestones : null;
};

const getIsITRRequired = (
  commodities: any,
  isClaimAction: boolean,
  isAllMilestonesAction: boolean,
  milestoneName?: string
) => {
  if (!commodities || !commodities.length) return false;
  const pendingApproveMilestones = uniqBy(
    flatMap(
      commodities.map((commodity: MaterialCommodity) => {
        return commodity.milestones.filter((milestone) => {
          return (
            milestone.currentStatus.codeI18n === MilestoneStatusCode.CLAIMED &&
            (isAllMilestonesAction ? true : milestone.name === milestoneName)
          );
        });
      })
    ),
    'name'
  );

  const pendingClaimMilestones = uniqBy(
    flatMap(
      commodities.map((commodity: MaterialCommodity) => {
        return commodity.milestones.filter((milestone) => {
          const hasOpenBlocks = some(milestone.blocks, { currentStatusI18n: BlockStatusCode.BLOCK_OPENED });

          return (
            !hasOpenBlocks &&
            milestone.currentStatus.codeI18n !== MilestoneStatusCode.CLAIMED &&
            milestone.currentStatus.codeI18n !== MilestoneStatusCode.REJECTED &&
            milestone.currentStatus.codeI18n !== MilestoneStatusCode.APPROVED &&
            (isAllMilestonesAction ? true : milestone.name === milestoneName) &&
            (milestone.weldStatuses && milestone.weldStatuses.length > 0
              ? every(milestone.weldStatuses, (weldStatus) => {
                  return weldStatus?.status?.status === CommodityStatusCode.VT_APPROVED && !weldStatus?.itrRequired;
                })
              : true)
          );
        });
      })
    ),
    'name'
  );

  //@ts-ignore always be milestone array
  const commodityMilestones: CommodityMilestone[] = isClaimAction ? pendingClaimMilestones : pendingApproveMilestones;
  if (isAllMilestonesAction) {
    const milestoneTypes: any = flatMap(commodityMilestones, (item: CommodityMilestone) => {
      return item.type;
    });
    return some(milestoneTypes, (item: ProjectMilestoneType) => {
      return (
        item &&
        item.projectItrConfig &&
        some(
          item.projectItrConfig,
          (itrConfig) =>
            itrConfig.itrType === ItrType.PIPE_CLEANLINESS && itrConfig.itrMilestoneAction === ItrMilestoneAction.CLAIM
        )
      );
    });
  } else {
    return some(commodityMilestones[0]?.type.projectItrConfig, (item) => {
      return item.itrType === ItrType.PIPE_CLEANLINESS && item.itrMilestoneAction === ItrMilestoneAction.CLAIM;
    });
  }
};

const getIsITRRequiredForApproveAll = (commodities: any, isAllMilestonesAction: boolean, milestoneName?: string) => {
  if (!commodities || !commodities.length) return false;
  const pendingApproveMilestones: CommodityMilestone[] = uniqBy(
    flatMap(
      commodities.map((commodity: MaterialCommodity) => {
        return commodity.milestones.filter((milestone) => {
          return (
            milestone.currentStatus.codeI18n === MilestoneStatusCode.CLAIMED &&
            (isAllMilestonesAction ? true : milestone.name === milestoneName)
          );
        });
      })
    ),
    'name'
  );

  //@ts-ignore always be milestone array
  if (isAllMilestonesAction) {
    const milestoneTypes: any = flatMap(pendingApproveMilestones, (item: CommodityMilestone) => {
      return item.type;
    });
    return some(milestoneTypes, (item: ProjectMilestoneType) => {
      return (
        item &&
        item.projectItrConfig &&
        some(item.projectItrConfig, (itrConfig) => {
          return checkItrCondition(itrConfig);
        })
      );
    });
  } else {
    return some(pendingApproveMilestones[0]?.type.projectItrConfig, (item) => {
      return checkItrCondition(item);
    });
  }
};

const checkItrCondition = (item: ProjectItrConfig) => {
  return item.itrType === ItrType.PIPE_CLEANLINESS && item.itrMilestoneAction === ItrMilestoneAction.APPROVE;
};
export const MultipleCommodityAction: FC<MultipleCommodityActionProps> = ({
  close,
  actionMilestone,
  commodityType,
}) => {
  const { id: workPackageId } = useWorkPackage();
  const [isCertified, setIsCertified] = useState(false);
  const { action, milestoneTypeId, milestoneName } = actionMilestone;
  const isClaimAction = toLower(action) === t`claim`;
  const isApproveAction = toLower(action) === t`approve`;
  const isAllMilestonesAction = isEmpty(milestoneTypeId);
  const [totalCommodities, setTotalCommodities] = useState(0);

  const { loading, error, data, refetch, networkStatus, fetchMore } = useQuery<
    { commodityList: CommodityList },
    {
      input: CommodityFilterInput;
      paging: PagingInput;
    }
  >(GET_COMMODITIES, {
    variables: {
      input: {
        complexFilter: {
          and: [
            { term: CommodityFilterByTerm.WORK_PACKAGE_ID, value: workPackageId, operator: FilterOperator.EQUALS },
            {
              term: CommodityFilterByTerm.COMMODITY_TYPE_ID,
              value: commodityType.id,
              operator: FilterOperator.EQUALS,
            },
            { term: CommodityFilterByTerm.EXCLUDE_CONNECTED_COMMODITY, value: 'true', operator: FilterOperator.EQUALS },
          ],
        },
        sortByFilter: {
          term: CommoditySortByTerm.TAG,
          sortOrder: SortOrder.ASC,
        },
      },
      paging: { cursor: '1', limit: commodityPageSize },
    },
    notifyOnNetworkStatusChange: true,
  });
  const commodities = useMemo(() => get(data, 'commodities.items', []), [data]);
  const commodityCount = get(data, 'commodities.total');
  useEffect(() => {
    if (commodityCount && commodityCount !== totalCommodities) {
      setTotalCommodities(commodityCount);
    }
  }, [commodityCount]);
  const cursor = get(data, 'commodities.cursor');

  useEffect(() => {
    if (commodities?.length < totalCommodities) {
      fetchMore({
        variables: {
          paging: { cursor: cursor, limit: commodityPageSize },
        },
      });
    }
  }, [fetchMore, totalCommodities, commodities?.length, cursor]);

  const filteredCommodities = useGroupedCommodities(commodities)[commodityType?.id];
  const itrRequired = getIsITRRequired(filteredCommodities, isClaimAction, isAllMilestonesAction, milestoneName);

  const itrRequiredForApproveAll = getIsITRRequiredForApproveAll(
    filteredCommodities,
    isAllMilestonesAction,
    milestoneName
  );

  const input = {
    workPackageId: workPackageId,
    milestoneTypeId: milestoneTypeId,
    commodityTypeId: commodityType.id,
  };

  const [
    claimAllMilestones,
    { loading: claimAllMilestonesLoading, data: claimAllMilestonesdata, error: claimAllMilestonesError },
  ] = useMutation<{ claimAllMilestones: ClaimAllMilestonesPayload }, { input: ClaimAllMilestonesInput }>(
    CLAIM_ALL_MILESTONES,
    {
      onCompleted: (data) => {
        if (!getClaimAllErroredMilestones(data.claimAllMilestones?.erroredMilestones)) {
          close();
        }
      },
    }
  );

  const [
    approveAllMilestones,
    { loading: approveAllMilestonesLoading, data: approveAllMilestonesData, error: approveAllMilestonesError },
  ] = useMutation<{ approveAllMilestones: ApproveAllMilestonesPayload }, { input: ApproveAllMilestonesInput }>(
    APPROVE_ALL_MILESTONES,
    {
      onCompleted: (data) => {
        if (!getApproveAllErroredMilestones(data.approveAllMilestones?.erroredMilestones)) {
          close();
        }
      },
    }
  );

  const isMutationInProgress = approveAllMilestonesLoading || claimAllMilestonesLoading;
  const milestoneError = isClaimAction
    ? getClaimAllErroredMilestones(get(claimAllMilestonesdata, 'claimAllMilestones.erroredMilestones')) ||
      claimAllMilestonesError
    : getApproveAllErroredMilestones(get(approveAllMilestonesData, 'approveAllMilestones.erroredMilestones')) ||
      approveAllMilestonesError;

  const isLoading = loading || networkStatus === NetworkStatus.fetchMore || networkStatus === NetworkStatus.refetch;
  return (
    <>
      {milestoneError && (
        <BulkActionError action={isClaimAction ? t`claimed` : t`approved`} onClose={close}></BulkActionError>
      )}

      {!milestoneError && (
        <Modal visible closable onDismiss={close} size={ModalSize.LARGE}>
          <ModalHeaderStyled title={t`Confirmation`}>
            {/*TODO button size need to fix from gds */}
            <ModalClose onClick={close}></ModalClose>
          </ModalHeaderStyled>
          {error && (
            <Box
              css="height: 100%;"
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'center' }}
              flexDirection={{ sm: 'column' }}
              p={{ sm: '600' }}
            >
              <Box pb={{ sm: '500' }} flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }}>
                <Text>
                  <Trans>There was a problem loading the list of commodity milestones.</Trans>
                </Text>
                <Text>
                  <Trans>Please click the button below to try again.</Trans>
                </Text>
              </Box>
              <Box>
                <Button size={ButtonSize.LARGE} onClick={() => refetch()}>
                  <Box mr={{ sm: '400' }}>
                    <Icon icon="undo" />
                  </Box>
                  Retry
                </Button>
              </Box>
            </Box>
          )}
          {isLoading && !error && (
            <Box
              css="height: 100%;"
              data-testid="loading"
              p={{ sm: '600' }}
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'center' }}
            >
              <Spinner size={SpinnerSize.LARGE} />
            </Box>
          )}
          {!isLoading && !error && filteredCommodities && (
            <>
              <ModalSubHeader>
                <Text family="graphik" size="500" color="grey.1000" weight="600">
                  {t`Are you sure you want to`} {toLower(action)} {t`all`}{' '}
                  {isAllMilestonesAction ? (isClaimAction ? t`milestones` : t`claimed milestones`) : milestoneName}?
                </Text>
              </ModalSubHeader>

              <ModalBodyStyled
                css={`
                  flex-direction: column;
                `}
              >
                {filteredCommodities.map((commodity: MaterialCommodity) => {
                  const impactedMilestones = commodity.milestones.filter((milestone) => {
                    const hasOpenBlocks = some(milestone.blocks, { currentStatusI18n: BlockStatusCode.BLOCK_OPENED });
                    return (
                      !hasOpenBlocks &&
                      ((isClaimAction &&
                        milestone.currentStatus.codeI18n !== MilestoneStatusCode.CLAIMED &&
                        milestone.currentStatus.codeI18n !== MilestoneStatusCode.REJECTED &&
                        milestone.currentStatus.codeI18n !== MilestoneStatusCode.APPROVED &&
                        (milestone.weldStatuses && milestone.weldStatuses.length > 0
                          ? every(milestone.weldStatuses, (weldStatus) => {
                              return weldStatus?.status?.status === CommodityStatusCode.VT_APPROVED;
                            })
                          : true)) ||
                        (isApproveAction && milestone.currentStatus.codeI18n === MilestoneStatusCode.CLAIMED)) &&
                      (isAllMilestonesAction ? true : milestone.name === milestoneName)
                    );
                  });

                  return map(impactedMilestones, (milestone: CommodityMilestone) => {
                    return (
                      <CommodityMilestoneStyled key={milestone.name}>
                        <Box pb={{ sm: '400' }} pt={{ sm: '100' }}>
                          <Text size="500" family="graphik" color="grey.1000" weight="600">
                            {commodityType.name}:&nbsp;
                          </Text>
                          <Text size="500" family="graphik" color="grey.1000">
                            {commodity.tag}
                          </Text>
                        </Box>
                        <Box pb={{ sm: '100' }}>
                          <Text size="500" family="graphik" color="grey.1000" weight="600">
                            <Trans>Milestone</Trans>:&nbsp;
                          </Text>
                          <Text size="500" family="graphik" color="grey.1000">
                            {milestone.name}
                          </Text>
                        </Box>
                      </CommodityMilestoneStyled>
                    );
                  });
                })}
              </ModalBodyStyled>
              {((itrRequired && isClaimAction) || (itrRequiredForApproveAll && isApproveAction)) && (
                <ModalSubFooter>
                  <Box mr={{ sm: '500' }} alignItems={{ sm: 'center' }}>
                    <Checkbox
                      checked={isCertified}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setIsCertified(e.target.checked)}
                      readOnly
                    />
                  </Box>
                  <Box flexGrow={{ sm: 1 }} justifyContent={{ sm: 'space-between' }} data-testid="label-selectAll">
                    <Text size="500" family="graphik" color="grey.1000">
                      <Trans>I certify that piping internal cleanliness was acceptable</Trans> <br />
                      <Trans>prior to completing all piping connections.</Trans>
                    </Text>
                  </Box>
                </ModalSubFooter>
              )}
            </>
          )}
          <ModalFooter>
            <Box justifyContent={{ sm: 'flex-end' }}>
              <Box mr={{ sm: '400' }}>
                <Button
                  appearance={ButtonAppearance.SECONDARY}
                  onClick={close}
                  css={`
                    padding-left: ${({ theme }) => theme.spacings['600']};
                    padding-right: ${({ theme }) => theme.spacings['600']};
                  `}
                  disabled={isMutationInProgress}
                >
                  <Trans> Cancel</Trans>
                </Button>
              </Box>
              <Box>
                <Button
                  type="submit"
                  appearance={ButtonAppearance.SECONDARY}
                  onClick={() => {
                    isClaimAction
                      ? claimAllMilestones({
                          variables: {
                            input: { ...input, itrRequired: itrRequired },
                          },
                        })
                      : approveAllMilestones({
                          variables: {
                            input: { ...input, itrRequired: itrRequiredForApproveAll },
                          },
                        });
                  }}
                  css={`
                    padding-left: ${({ theme }) => theme.spacings['600']};
                    padding-right: ${({ theme }) => theme.spacings['600']};
                  `}
                  disabled={isMutationInProgress || (itrRequired && isClaimAction && !isCertified)}
                  data-testid="confirm-button"
                >
                  {!isMutationInProgress && <Trans>Confirm</Trans>}
                  {isMutationInProgress && <Spinner></Spinner>}
                </Button>
              </Box>
            </Box>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default MultipleCommodityAction;
